import { lazy, Suspense, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useEddyMachineStatus } from '~/hooks/queries/use-feature-flags';
import { closeOperatorPanel, openOperatorPanel, useOperatorPanelStore } from '~/lib/stores/operator-panel-store';
import { useCloseOnProBuilderOpen } from '~/lib/stores/use-close-on-pro-builder-open';
import { handleOperatorRunDeepLink } from './operator-run-deep-link';

/**
 * FRONT-1991: mounted exactly ONCE, in `app-layout.tsx`, next to
 * `CommandPalette`/`InspectorPanel`/`DevConsole` — same "one global overlay
 * host per feature" shape those already use. `desktop-pane-connect.ts`
 * already keeps `@novnc/novnc` out of the main bundle; lazy-loading the
 * panel ITSELF on top of that keeps the transcript/panel chrome code out of
 * every page load too, fetched only the first time a request actually
 * appears. A source-guard test (`operator-panel-host.test.ts`) proves this
 * file is mounted exactly once across the app.
 */
const OperatorPanel = lazy(() =>
  import('./operator-panel').then((m) => ({ default: m.OperatorPanel })),
);

export function OperatorPanelHost() {
  const machineStatus = useEddyMachineStatus();
  const enabled = machineStatus === 'enabled';
  const request = useOperatorPanelStore((s) => s.request);
  // FRONT-3003: modal viewers are mutually exclusive. The Pro Builder window
  // sits at the same z-index and mounts BEFORE this host, so it would open
  // underneath the panel while its own focus moved the keyboard into it: the
  // panel closes the instant Pro Builder opens (it hides on a new panel
  // request in turn).
  useCloseOnProBuilderOpen(closeOperatorPanel);

  // FRONT-1996: "Astra needs your help" links here as `/chat?operatorRun=
  // <runId>` (`OPERATOR_RUN_DEEP_LINK_PARAM`) — opened once, then stripped
  // from the URL (`replace`, no new history entry) so a refresh or a
  // back-navigation can't reopen it. Reacts to `searchParams` rather than
  // running once on mount: this component itself mounts exactly once for
  // the whole app (see the source-guard test above it), so an in-app
  // navigation that changes the URL without remounting anything must still
  // be caught. `machineStatus` (FRONT-2278, replacing review m14's plain
  // `enabled` boolean) is threaded through so a stale link can never open
  // the panel for a caller the machine feature isn't on for, and — the
  // reason a third state exists at all — so a COLD navigation's first
  // render, where the flags query hasn't answered yet, doesn't get read as
  // "off" and strip the param before the flag ever comes back on. See
  // `handleOperatorRunDeepLink`'s own doc for the three-state decision
  // itself (review m13) — this effect just wires it to the real
  // `openOperatorPanel`/`setSearchParams`.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    handleOperatorRunDeepLink({ status: machineStatus, searchParams, openPanel: openOperatorPanel, setSearchParams });
  }, [machineStatus, searchParams, setSearchParams]);

  if (!enabled || !request) return null;
  return (
    <Suspense fallback={null}>
      {/* Keyed by runId so switching from one run's panel to another (or to
          "no particular run") remounts the pane/transcript with fresh state,
          same reasoning as `key={active.id}` on the chat modal body. */}
      <OperatorPanel key={request.runId ?? 'none'} runId={request.runId} />
    </Suspense>
  );
}
